import { Box, Button, ThemeProvider, Typography, useTheme } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from 'react-toastify'
import { observer } from 'mobx-react'

import DiscussionMeetingParticipationLayout from '../../components/webinar-room/DiscussionMeetingParticipationLayout.jsx'
import WebinarMeetingParticipationLayout from '../../components/webinar-room/WebinarMeetingParticipationLayout.jsx'
import MeetingStoreInstance, { MeetingStoreProvider, useMeetingStore } from '../../store/meeting.store'
import MeetingDetailsCopyDialog from '../../components/webinar-room/MeetingDetailsCopyDialog.jsx'
import PresentationsManager from '../../components/webinar-room/PresentationsManager.jsx'
import SocketsContextProvider, { useSocketsContext } from '../../contexts/sockets.context'
import JoinMeetingHeader from '../../components/webinar-room/JoinMeetingHeader.jsx'
import LeftSideCollapse from '../../components/webinar-room/LeftSideCollapse.jsx'
import JoinMeetingSetup from '../../components/webinar-room/JoinMeetingSetup.jsx'
import TryAgainMessageBlock from '../../components/common/TryAgainMessageBlock'
import NewRequestsList from '../../components/webinar-room/NewRequestsList.jsx'
import PollManager from '../../components/webinar-room/polls/PollManager.jsx'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import notificationSound from '../../assets/audios/NotificationDing.wav'
import VotePoll from '../../components/webinar-room/polls/VotePoll.jsx'
import ActionsBar from '../../components/webinar-room/ActionsBar.jsx'
import connectionLostImg from '../../assets/no-connection.svg'
import { useUserStore } from '../../store/user.store'
import api from '../../service/api'

import VitualboardStoreInstance, { useVirtualboardStore, VirtualboardProvider } from '../../store/virtualboard.store.jsx'
import MeetingActionsContextProvider, { useMeetingActionsContext } from '../../contexts/meetingactions.context.js'
import HybridClassParticipationLayout from '../../components/webinar-room/HybridClassParticipationLayout.jsx'
import pictureInPictureImg from '../../assets/picture-in-picture.svg'
import WebinarPipPortal from '../../portals/WebinarPipPortal.jsx'
import Header from '../../components/webinar-room/Header.jsx'
import themes from '../../themes/themes.js'
import { OnlineSessionsSocketStoreProvider, useOnlineSessionsSocketStore } from '../../store/onlinesessionsocket.store.js'
import { usePip } from '../../hooks/usePip.js'
import ReconnectHandler from '../../components/webinar-room/ReconnectHandler.jsx'

const RoomComponent = observer( () => {

    const [isManagingPoll, setIsManagingPoll] = useState( false )
    const [showCopyDialog, setShowCopyDialog] = useState( false )
    const [loading, setLoading] = useState( true )
    const [error, setError] = useState( null )

    const { meeting_id } = useParams()
    const { palette } = useTheme()

    const navigate = useNavigate()

    const VirtualboardStore = useVirtualboardStore()
    const MeetingStore = useMeetingStore()
    const UserStore = useUserStore()

    const OnlineSessionSocketStore = useOnlineSessionsSocketStore()
    const socketServerDisconnected = OnlineSessionSocketStore.isDisconnected


    const { safeRejoinMeeting, connectionLost, agoraServerDisconnected, turnOnCamera, turnOnMicrophone, leaveChannel, toggleScreenShare, toggleScreenShareAudio, toggleScreenRecording, replaceSharingTrack, stopScreenShare, webinarPipWindow } = useMeetingActionsContext()
    // const { pipWindow } = usePip()
    // console.log( pipWindow )


    const clientRef = useRef()
    const initialRequest = useRef( true )
    const recorderIntervalRef = useRef()
    const webinarWrapperRef = useRef()
    const pollDialogAnchor = useRef()
    const webinarContainer = useRef()
    const containerRef = useRef()


    const leavePipWindow = async () => {
        try {
            await webinarPipWindow?.close()
        } catch ( err ) {
            console.log( err )
        }
    }

    const handleVisibilityChange = useCallback( async ( event ) => {
        if ( MeetingStore.getMeetingState.isJoined ) {

            try {
                if ( document.hidden ) return

                console.log( "🔁 Visibility restored — verifying Agora & Socket" )

                const agoraClient = window._agoraClientRef

                const socketDisconnected = OnlineSessionSocketStore.isDisconnected
                const agoraDisconnected = agoraClient?.connectionState === "DISCONNECTED"

                if ( socketDisconnected ) {
                    await safeRejoinMeeting( true )
                } else {
                    console.log( "✅ All connections alive; no rejoin needed." )
                }
            } catch ( err ) {
                console.log( err )
            }
        }
    }, [safeRejoinMeeting, OnlineSessionSocketStore, MeetingStore] )

    const navigateToFeedback = async () => {
        try {
            await leaveChannel()
            await OnlineSessionSocketStore.disconnect()
        } catch ( err ) { }
        navigate( 'feedback' )
    }

    const openMeetingInfoDialog = () => {
        setShowCopyDialog( true )
    }


    const fetchBasicDetails = useCallback( async ( showLoading = false ) => {
        try {
            if ( initialRequest.current || showLoading )
                setLoading( true )
            const meetingFetchData = await MeetingStore.fetchMeeting( meeting_id, { accessDetails: initialRequest.current ? true : false, settings: initialRequest.current ? true : false } )
            initialRequest.current = false
            if ( !meetingFetchData.unauth ) {
                const { success: userFetchSuccess } = await UserStore.fetchUser()
                if ( meetingFetchData.hasAccess ) {
                    if ( userFetchSuccess ) {
                        const { data: { data: { token, agoraChannelToken, agoraChannelShareToken } } } = await api.onlineMeetings.getMeetingAccessToken( meeting_id, UserStore.getUser )
                        sessionStorage.setItem( 'meetingAccessToken', JSON.stringify( { meeting_id: meeting_id, token, agoraChannelToken, agoraChannelShareToken } ) )
                    }

                    if ( OnlineSessionSocketStore.isDisconnected ) {
                        console.log( "Connecting to socket server.." )
                        await OnlineSessionSocketStore.connect( meeting_id, UserStore.getUser.user_id )
                        console.log( "Connected to socket server.." )
                    }

                    if ( userFetchSuccess ) {
                        const localUserData = {
                            name: UserStore.getUser.name || UserStore.getUser.student_name || UserStore.getUser.employee_name || UserStore.getUser.user_name,
                            email: UserStore.getUser.email,
                            type: "local-client",
                            user_id: UserStore.getUser.user_id,
                            role: UserStore.getUser.user_role
                        }
                        MeetingStore.setLocalUserDetails( localUserData )
                    }
                }
            } else if ( meetingFetchData.unauth && meetingFetchData.hasAccess ) {
                if ( OnlineSessionSocketStore.isDisconnected ) {
                    console.log( "Connecting to socket server.." )
                    await OnlineSessionSocketStore.connect( meeting_id, "" )
                    console.log( "Connected to socket server.." )
                }
            }
            if ( meetingFetchData.success ) {
                setError( null )
            } else {
                setError( meetingFetchData )
            }
        } catch ( err ) {
            console.log( err )
            if ( err.message === "No valid token found" ) {
                setError( null )
            } else
                setError( { code: err?.response?.status, message: err?.response?.data?.message || "Failed to establish connection please try again" } )
        } finally {
            setLoading( false )
        }
    }, [MeetingStore, UserStore, meeting_id, OnlineSessionSocketStore] )

    useEffect( () => {
        fetchBasicDetails()
    }, [fetchBasicDetails] )

    useEffect( () => {
        const unsubNewRoomMsgEvent = OnlineSessionSocketStore.on( "chat:new-room-message", ( msg ) =>
            MeetingStore.setRoomChats( msg, msg.chat_id )
        )

        const unsubNewPrivateMsgEvent = OnlineSessionSocketStore.on( "chat:new-private-message", ( msg ) =>
            MeetingStore.setRoomChats( msg, msg.chat_id )
        )

        const unsubNewUserJoinEvent = OnlineSessionSocketStore.on( "new-user-join", ( user ) => {
            console.log( "➡️ New user joined:", user.user_id )
        } )

        const unsubUserListUpdatedEvent = OnlineSessionSocketStore.on( "user-list-updated", ( { currentUsers, action } ) => {
            MeetingStore.addOrUpdateRemoteUsers( currentUsers, "alive-socket-server", action )
            MeetingStore.updateMeetingUsersDetails( currentUsers )
        }
        )

        const unsubUserLeftEvent = OnlineSessionSocketStore.on( "user-left", ( remoteUser ) => {
            MeetingStore.removeRemoteUser( remoteUser.uid, "sockets user-left" )
            if ( remoteUser.uid.includes( "sharing" ) ) {
                MeetingStore.addOrRemoveSharingUsers( remoteUser, "removed" )
            }
        } )

        const unsubUpdateHandRaiseQueueEvent = OnlineSessionSocketStore.on( "update-hand-raise-queue", ( { handRaises } ) => {
            MeetingStore.setRaisedHands( handRaises )
            MeetingStore.setMeetingRoomLayoutSettings( {
                hideHandsRaisedCard: false,
            } )
        } )

        const unsubExtendedSessionTimeEvent = OnlineSessionSocketStore.on( "extended-meeting-time", ( { data } ) =>
            MeetingStore.setMeetingDetails( data )
        )

        const unsubNewVbNewEventEvent = OnlineSessionSocketStore.on( "virtualboard:new-event", ( { event, elements, page_id } ) => {
            if ( event.type !== "zoom-and-pan" ) {
                // MeetingStore.setPresentedDocElements( page_id, elements )
                VirtualboardStore.addEvent( event )
            } else
                MeetingStore.setReadOnlyZoomAndPan( event.zoom, event.pan )

        } )

        const unsubDuplicateSessionEvent = OnlineSessionSocketStore.on( 'duplicate-session', () => {
            MeetingStore.setMeetingState( { duplicateSession: true } )
        } )

        const unsubPollUpdatedEvent = OnlineSessionSocketStore.on( 'poll:updated', ( { poll, id } ) => {
            MeetingStore.updatePoll( poll, id )
            MeetingStore.setPollResults( poll )
        } )

        const unsubPollCreatedEvent = OnlineSessionSocketStore.on( 'poll:created', ( { poll, user } ) => {
            if ( MeetingStore.getLocalUser.meeting_role === "viewer" ) {
                MeetingStore.setMeetingRoomLayoutSettings( {
                    isPollDialogMinimized: false,
                } )
                MeetingStore.addOrRemovePoll( poll )
            } else if ( MeetingStore.getLocalUser.meeting_role === "meeting-host" ) {
                MeetingStore.addOrRemovePoll( poll )
                toast( `${user.name} created new poll.` )
            }
        } )


        const unsubMessageEvent = OnlineSessionSocketStore.on( 'message', ( message ) => toast( message ) )

        return () => {
            unsubNewRoomMsgEvent()
            unsubNewPrivateMsgEvent()
            unsubNewUserJoinEvent()
            unsubUserListUpdatedEvent()
            unsubUserLeftEvent()
            unsubExtendedSessionTimeEvent()
            unsubNewVbNewEventEvent()
            unsubDuplicateSessionEvent()
            unsubUpdateHandRaiseQueueEvent()
            unsubMessageEvent()
            unsubPollCreatedEvent()
            unsubPollUpdatedEvent()
        }

    }, [MeetingStore, OnlineSessionSocketStore, VirtualboardStore] )

    useEffect( () => {
        document.addEventListener( 'visibilitychange', handleVisibilityChange )
        // document.addEventListener( 'online', handleVisibilityChange )
        return () => {
            document.removeEventListener( 'visibilitychange', handleVisibilityChange )
            // document.removeEventListener( 'online', handleVisibilityChange )
        }
    }, [handleVisibilityChange] )


    return (
        <Box id="webinar-wrapper" flexGrow={1} display="flex" flexDirection="column" ref={webinarWrapperRef} bgcolor={palette.background.paper} overflow="auto">

            {MeetingStore.getMeetingState.isJoined && <ReconnectHandler />}

            {!webinarPipWindow && <Box flexGrow={1} ref={webinarContainer} sx={{ ":focus": { outline: "none", border: "none" } }} tabIndex={0} overflow="auto" id="webinar-container" display="flex" flexDirection="column" bgcolor={palette.background.paper} className="room-container">
                {/* Audio Player */}
                <Box id="chatSound" component="audio" src={notificationSound} preload='auto' />

                {/* Meeting Details */}
                {!loading && showCopyDialog && <MeetingDetailsCopyDialog container={webinarContainer} open={showCopyDialog} setOpen={setShowCopyDialog} meeting={MeetingStore.getCurrentMeeting} />}

                {/* Polls Manager */}
                {!connectionLost && !( socketServerDisconnected || agoraServerDisconnected ) && MeetingStore.getMeetingState.isJoined && <PollManager container={webinarContainer} open={isManagingPoll} setOpen={setIsManagingPoll} />}

                {/* Poll Voter */}
                {!connectionLost && !( socketServerDisconnected || agoraServerDisconnected ) && MeetingStore?.getLocalUser?.meeting_role === 'viewer' && MeetingStore.getMeetingState.isJoined && MeetingStore.getPolls.length > 0 && !MeetingStore.getMeetingRoomLayoutSettings.isPollDialogMinimized && <VotePoll anchorEl={pollDialogAnchor} open={!MeetingStore.getMeetingRoomLayoutSettings.isPollDialogMinimized} poll={MeetingStore.getPolls[MeetingStore.getMeetingState.currentPollIndex]} />}

                {/* Presentation Manager */}
                {!connectionLost && !( socketServerDisconnected || agoraServerDisconnected ) && MeetingStore.getMeetingState.isJoined && MeetingStore?.getLocalUser?.meeting_role === 'meeting-host' && <PresentationsManager toggleScreenShare={toggleScreenShare} container={webinarContainer} />}

                {!loading && !connectionLost && !( socketServerDisconnected || agoraServerDisconnected ) && !error && !MeetingStore.getMeetingState.isJoined && <Box display="flex" height={{ md: "100%", xs: "auto" }} flexDirection="column" >
                    <JoinMeetingHeader />
                    <JoinMeetingSetup openMeetingInfoDialog={openMeetingInfoDialog} error={error} />
                </Box>}

                {!loading && !( socketServerDisconnected || agoraServerDisconnected ) && !error && MeetingStore.getMeetingState.isJoined && <Box display="flex" id="meeting-area" flexGrow={1} overflow="hidden" gap="10px" flexDirection="column" height="calc(100% - 20px)" padding="10px" >
                    <Header meetingContainer={webinarContainer} />
                    <Box id="participation" minHeight="0" flexGrow={1} ref={containerRef} width="100%" display="flex" flexShrink={1} >
                        <NewRequestsList container={containerRef} />
                        {MeetingStore.getCurrentMeeting.meeting_type === 'discussion' && <DiscussionMeetingParticipationLayout toggleScreenShareAudio={toggleScreenShareAudio} />}
                        {MeetingStore.getCurrentMeeting.meeting_type === 'webinar' && <WebinarMeetingParticipationLayout toggleScreenShareAudio={toggleScreenShareAudio} toggleScreenShare={toggleScreenShare} />}
                        {MeetingStore.getCurrentMeeting.meeting_type === 'class' && <WebinarMeetingParticipationLayout toggleScreenShareAudio={toggleScreenShareAudio} toggleScreenShare={toggleScreenShare} />}
                        {MeetingStore.getCurrentMeeting.meeting_type === 'hybrid-class' && <HybridClassParticipationLayout toggleScreenShareAudio={toggleScreenShareAudio} toggleScreenShare={toggleScreenShare} />}
                        <LeftSideCollapse />
                    </Box >
                    <ActionsBar replaceSharingTrack={replaceSharingTrack} meetingContainer={webinarContainer} openMeetingInfoDialog={openMeetingInfoDialog} toggleScreenRecording={toggleScreenRecording} stopScreenShare={stopScreenShare} recorderIntervalRef={recorderIntervalRef} leaveChannel={leaveChannel} toggleScreenShare={toggleScreenShare} setIsManagingPoll={setIsManagingPoll} client={clientRef.current} turnOnCamera={turnOnCamera} turnOnMicrophone={turnOnMicrophone} />
                </Box>}

                {
                    !loading && ( socketServerDisconnected || agoraServerDisconnected ) && <Box display="flex" flexDirection="column" height="100%" alignItems="center" gap="20px" padding="20px" justifyContent="center">
                        <Box component="img" src={connectionLostImg} width="200px" />
                        <Typography textAlign="center" >Oops! you got disconnected! Connection to server is interupted!</Typography>
                        <Button variant='outlined' onClick={navigateToFeedback} >Re-Join</Button>
                    </Box>
                }

                {
                    loading && !error && <Box display="flex" height="100%" alignItems="center" justifyContent="center">
                        <DataLoadingSpinner size={30} fontSize="30px" waitingMessage="Loading session details please wait..." />
                    </Box>
                }

                {
                    error && <Box display="flex" height="100%" alignItems="center" justifyContent="center">
                        <TryAgainMessageBlock code={error.status} getData={fetchBasicDetails} err={error.message} />
                    </Box>
                }
            </Box >}

            {webinarPipWindow && <WebinarPipPortal pipWindow={webinarPipWindow}>
                <Box flexGrow={1} ref={webinarContainer} sx={{ ":focus": { outline: "none", border: "none" } }} tabIndex={0} overflow="auto" id="webinar-container" display="flex" flexDirection="column" bgcolor={palette.background.paper} className="room-container">
                    {/* Audio Player */}
                    <Box id="chatSound" component="audio" src={notificationSound} preload='auto' />
                    {/* Meeting Details */}
                    {!loading && showCopyDialog && <MeetingDetailsCopyDialog container={webinarContainer} open={showCopyDialog} setOpen={setShowCopyDialog} meeting={MeetingStore.getCurrentMeeting} />}
                    {/* Polls Manager */}
                    {!connectionLost && !( socketServerDisconnected || agoraServerDisconnected ) && MeetingStore.getMeetingState.isJoined && <PollManager container={webinarContainer} open={isManagingPoll} setOpen={setIsManagingPoll} />}
                    {/* Poll Voter */}
                    {!connectionLost && !( socketServerDisconnected || agoraServerDisconnected ) && MeetingStore?.getLocalUser?.meeting_role === 'viewer' && MeetingStore.getMeetingState.isJoined && MeetingStore.getPolls.length > 0 && !MeetingStore.getMeetingRoomLayoutSettings.isPollDialogMinimized && <VotePoll anchorEl={pollDialogAnchor} open={!MeetingStore.getMeetingRoomLayoutSettings.isPollDialogMinimized} poll={MeetingStore.getPolls[MeetingStore.getMeetingState.currentPollIndex]} />}
                    {/* Presentation Manager */}
                    {!connectionLost && !( socketServerDisconnected || agoraServerDisconnected ) && MeetingStore.getMeetingState.isJoined && MeetingStore?.getLocalUser?.meeting_role === 'meeting-host' && <PresentationsManager toggleScreenShare={toggleScreenShare} container={webinarContainer} />}
                    {!loading && !connectionLost && !( socketServerDisconnected || agoraServerDisconnected ) && !error && !MeetingStore.getMeetingState.isJoined && <Box display="flex" height={{ md: "100%", xs: "auto" }} flexDirection="column" >
                        <JoinMeetingHeader />
                        <JoinMeetingSetup openMeetingInfoDialog={openMeetingInfoDialog} error={error} />
                    </Box>}
                    {!loading && !( socketServerDisconnected || agoraServerDisconnected ) && !error && MeetingStore.getMeetingState.isJoined && <Box display="flex" id="meeting-area" flexGrow={1} overflow="hidden" gap="10px" flexDirection="column" height="calc(100% - 20px)" padding="10px" >
                        <Header meetingContainer={webinarContainer} />
                        <Box id="participation" minHeight="0" flexGrow={1} ref={containerRef} width="100%" display="flex" flexShrink={1} >
                            <NewRequestsList container={containerRef} />
                            {MeetingStore.getCurrentMeeting.meeting_type === 'discussion' && <DiscussionMeetingParticipationLayout toggleScreenShareAudio={toggleScreenShareAudio} />}
                            {MeetingStore.getCurrentMeeting.meeting_type === 'webinar' && <WebinarMeetingParticipationLayout toggleScreenShareAudio={toggleScreenShareAudio} toggleScreenShare={toggleScreenShare} />}
                            {MeetingStore.getCurrentMeeting.meeting_type === 'class' && <WebinarMeetingParticipationLayout toggleScreenShareAudio={toggleScreenShareAudio} toggleScreenShare={toggleScreenShare} />}
                            {MeetingStore.getCurrentMeeting.meeting_type === 'hybrid-class' && <HybridClassParticipationLayout toggleScreenShareAudio={toggleScreenShareAudio} toggleScreenShare={toggleScreenShare} />}
                            <LeftSideCollapse />
                        </Box >
                        <ActionsBar replaceSharingTrack={replaceSharingTrack} meetingContainer={webinarContainer} openMeetingInfoDialog={openMeetingInfoDialog} toggleScreenRecording={toggleScreenRecording} stopScreenShare={stopScreenShare} recorderIntervalRef={recorderIntervalRef} leaveChannel={leaveChannel} toggleScreenShare={toggleScreenShare} setIsManagingPoll={setIsManagingPoll} client={clientRef.current} turnOnCamera={turnOnCamera} turnOnMicrophone={turnOnMicrophone} />
                    </Box>}
                    {
                        !loading && ( socketServerDisconnected || agoraServerDisconnected ) && <Box display="flex" flexDirection="column" height="100%" alignItems="center" gap="20px" padding="20px" justifyContent="center">
                            <Box component="img" src={connectionLostImg} width="200px" />
                            <Typography textAlign="center" >Oops! you got disconnected! Connection to server is interupted!</Typography>
                            <Button variant='outlined' onClick={navigateToFeedback} >Re-Join</Button>
                        </Box>
                    }
                    {
                        loading && !error && <Box display="flex" height="100%" alignItems="center" justifyContent="center">
                            <DataLoadingSpinner size={30} fontSize="30px" waitingMessage="Loading session details please wait..." />
                        </Box>
                    }
                    {
                        error && <Box display="flex" height="100%" alignItems="center" justifyContent="center">
                            <TryAgainMessageBlock code={error.status} getData={fetchBasicDetails} err={error.message} />
                        </Box>
                    }
                </Box >
            </WebinarPipPortal>}

            {webinarPipWindow && <Box display="flex" gap="20px" height="100%" flexDirection="column" alignItems="center" justifyContent="center">
                <Box component="img" src={pictureInPictureImg} width="180px" />
                <Typography variant='h6'>This session is being displayed in picture-in-picture mode!</Typography>
                <Button variant='outlined' sx={{ borderRadius: "20px" }} color='secondary' onClick={leavePipWindow} >Leave picture-in-picture mode</Button>

            </Box>}

        </Box>
    )
} )

const WebinarRoom = ( props ) => {

    const { palette } = useTheme()

    return (
        <ThemeProvider theme={themes.dark}>
            <Box bgcolor={palette.layoutBackground} display="flex" flexDirection="column" className={`viewport-height dark`}>
                <OnlineSessionsSocketStoreProvider>
                    <MeetingStoreProvider store={MeetingStoreInstance}>
                        <VirtualboardProvider store={VitualboardStoreInstance} >
                            {/* <SocketsContextProvider> */}
                            <MeetingActionsContextProvider>
                                <RoomComponent {...props} />
                            </MeetingActionsContextProvider>
                            {/* </SocketsContextProvider > */}
                        </VirtualboardProvider>
                    </MeetingStoreProvider>
                </OnlineSessionsSocketStoreProvider>
            </Box >
        </ThemeProvider>
    )
}

export default WebinarRoom
